import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * Ordered from the most specific phrase to the most general phrase.
 * Keep this order so "Security Camera" is not handled as a photography camera.
 */
const SCENE_SEARCH_RULES = [
  {
    aliases: ['video doorbell', 'doorbell camera', 'smart doorbell', '门铃监控', '门的监控', '门口摄像头'],
    query: 'Micro SD Card',
    guide: 'Home Security Storage Guide',
    heading: 'Recommended Micro SD Cards for Video Doorbells',
    description: 'Choose storage suitable for continuous video doorbell recording.',
  },
  {
    aliases: ['security camera', 'home security camera', 'home camera', 'cctv camera', 'cctv', 'surveillance camera', '家庭监控', '监控摄像头'],
    query: 'Micro SD Card',
    guide: 'Home Security Storage Guide',
    heading: 'Recommended Micro SD Cards for Security Cameras',
    description: 'Choose storage suitable for continuous home security recording.',
  },
  {
    aliases: ['dash cam', 'car camera', 'driving recorder', '行车记录仪', '车载摄像头'],
    query: 'Micro SD Card',
    guide: 'Dash Cam Storage Guide',
    heading: 'Recommended Micro SD Cards for Dash Cams',
    description: 'Choose a compatible memory card for continuous vehicle recording.',
  },
  {
    aliases: ['action camera', 'gopro', 'sports camera', '运动相机'],
    query: 'Micro SD Card',
    guide: 'Action Camera Storage Guide',
    heading: 'Recommended Micro SD Cards for Action Cameras',
    description: 'Choose fast, reliable storage for action and high-resolution video.',
  },
  {
    aliases: ['drone camera', 'aerial photography', 'drone', '无人机'],
    query: 'Micro SD Card',
    guide: 'Drone Storage Guide',
    heading: 'Recommended Micro SD Cards for Drones',
    description: 'Choose compact storage for aerial photography and video.',
  },
  {
    aliases: ['4k video', 'video recording', 'content creation', '视频拍摄'],
    query: 'Micro SD Card',
    guide: 'Video Recording Storage Guide',
    heading: 'Recommended Storage for Video Recording',
    description: 'Choose storage with the speed and capacity required by your recording format.',
  },
  {
    aliases: ['mirrorless camera', 'dslr', 'photography', 'camera', '相机摄影'],
    query: 'Micro SD Card',
    guide: 'Camera Storage Guide',
    heading: 'Recommended Micro SD Cards for Cameras',
    description: 'Choose storage for photography, burst shooting, and video capture.',
  },
  {
    aliases: ['playstation 5', 'console storage', 'ps5', 'ps5 扩容'],
    query: 'Gen 4 NVMe SSD',
    guide: 'PS5 Storage Upgrade Guide',
    heading: 'Recommended Gen 4 NVMe SSDs for PS5',
    description: 'Choose a PS5-compatible Gen 4 NVMe SSD with suitable cooling and capacity.',
  },
  {
    aliases: ['desktop gaming', 'game storage', 'gaming pc', '游戏电脑'],
    query: 'Gen 4 NVMe SSD',
    guide: 'Gaming PC Storage Guide',
    heading: 'Recommended Gen 4 NVMe SSDs for Gaming PCs',
    description: 'Choose high-speed storage for games, updates, and faster loading.',
  },
  {
    aliases: ['desktop pc', 'pc upgrade', 'computer storage', '台式电脑升级'],
    query: 'NVMe SSD',
    guide: 'Desktop PC Storage Upgrade Guide',
    heading: 'Recommended NVMe SSDs for Desktop PCs',
    description: 'Compare Gen 3 and Gen 4 NVMe SSDs for your desktop upgrade.',
  },
  {
    aliases: ['old laptop', 'sata upgrade', 'slow laptop', '老旧笔记本'],
    query: '2.5 inch SATA SSD',
    guide: 'Old Laptop Storage Upgrade Guide',
    heading: 'Recommended 2.5-inch SATA SSDs for Older Laptops',
    description: 'Upgrade an older compatible laptop with reliable solid-state storage.',
  },
  {
    aliases: ['laptop upgrade', 'notebook', 'laptop', '笔记本升级'],
    query: 'SSD',
    guide: 'Laptop Storage Upgrade Guide',
    heading: 'Recommended SSDs for Laptop Upgrades',
    description: 'Compare NVMe and SATA options supported by your laptop.',
  },
  {
    aliases: ['photo storage', 'photo backup', '照片备份'],
    query: 'Micro SD Card SSD',
    guide: 'Photo Storage and Backup Guide',
    heading: 'Recommended Storage for Photos and Backups',
    description: 'Compare portable memory cards and SSD storage for your photo workflow.',
  },
  {
    aliases: ['video editing', 'creative work', 'content creator', '视频剪辑'],
    query: 'Gen 4 NVMe SSD',
    guide: 'Video Editing Storage Guide',
    heading: 'Recommended Gen 4 NVMe SSDs for Video Editing',
    description: 'Choose fast storage for editing, cache files, and large media projects.',
  },
  {
    aliases: ['faster boot', 'speed up computer', 'slow pc', '快速启动'],
    query: 'SATA NVMe SSD',
    guide: 'Computer Speed Upgrade Guide',
    heading: 'Recommended SSDs for Faster Startup',
    description: 'Compare SATA and NVMe SSDs supported by your computer.',
  },
  {
    aliases: ['office', 'work', 'study', 'school', '办公学习'],
    query: 'SSD Micro SD Card',
    guide: 'Everyday Storage Guide',
    heading: 'Recommended Storage for Work and Study',
    description: 'Explore storage options for everyday files, schoolwork, and office use.',
  },
];

const COMPATIBILITY_NOTICE =
  'Check your device’s supported capacity, format, and performance requirements before purchasing.';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.refs.form?.addEventListener('submit', this.#handleSearchSubmit, { signal });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #getSceneSearch(searchTerm) {
    const normalizedTerm = searchTerm.trim().toLocaleLowerCase();
    return SCENE_SEARCH_RULES.find((rule) =>
      rule.aliases.some((alias) => normalizedTerm.includes(alias.toLocaleLowerCase()))
    );
  }

  #getSearchUrl(searchTerm) {
    const sceneSearch = this.#getSceneSearch(searchTerm);
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', sceneSearch?.query || searchTerm);
    if (sceneSearch) searchUrl.searchParams.set('scene', searchTerm);
    return searchUrl;
  }

  #handleSearchSubmit = (event) => {
    event.preventDefault();
    window.location.href = this.#getSearchUrl(this.refs.searchInput.value.trim()).toString();
  };

  #showSceneRecommendation(sceneSearch, originalSearchTerm) {
    this.refs.predictiveSearchResults.querySelector('.lufasnd-scene-search')?.remove();
    if (!sceneSearch) return;

    const panel = document.createElement('section');
    panel.className = 'lufasnd-scene-search';

    const eyebrow = document.createElement('p');
    eyebrow.className = 'lufasnd-scene-search__eyebrow';
    eyebrow.textContent = 'RELATED SUPPORT GUIDE';

    const title = document.createElement('p');
    title.className = 'lufasnd-scene-search__guide';
    title.textContent = sceneSearch.guide;

    const description = document.createElement('p');
    description.className = 'lufasnd-scene-search__description';
    description.textContent = sceneSearch.description;

    const productsLabel = document.createElement('p');
    productsLabel.className = 'lufasnd-scene-search__eyebrow lufasnd-scene-search__products-label';
    productsLabel.textContent = 'RECOMMENDED PRODUCTS';

    const heading = document.createElement('p');
    heading.className = 'lufasnd-scene-search__heading';
    heading.textContent = sceneSearch.heading;

    const compatibility = document.createElement('p');
    compatibility.className = 'lufasnd-scene-search__compatibility';
    compatibility.textContent = COMPATIBILITY_NOTICE;

    panel.dataset.originalQuery = originalSearchTerm;
    panel.append(eyebrow, title, description, productsLabel, heading, compatibility);
    this.refs.predictiveSearchResults.prepend(panel);
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          window.location.href = this.#getSearchUrl(this.refs.searchInput.value.trim()).toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const sceneSearch = this.#getSceneSearch(searchTerm);
    const effectiveSearchTerm = sceneSearch?.query || searchTerm;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', effectiveSearchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);
        this.#showSceneRecommendation(sceneSearch, searchTerm);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
